// src/components/WhatsNew.jsx
// "What's new" window: release highlights from src/releaseNotes.json.
// Everyone sees 'all' items; admins also see 'admin' items. 'internal'
// items are for CHANGELOG.md only and never shown here.
import { useEffect, useRef } from 'react';
import notes from '../releaseNotes.json';
import { timeZone, tzLabel } from '../utils/timeZone';
import Emo, { fluxNameOf } from './Emo';

// 3.55.0: icons named inside a note ('the 🛡️ badge') show as the portal's own icons
const INLINE = /(\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic})*)/u;
const withIcons = text => (typeof text !== 'string' ? text
  : text.split(INLINE).map((part, i) => (i % 2 && part !== '↩' && fluxNameOf(part) ? <Emo key={i} e={part} /> : part)));

export const RELEASES        = notes.releases;
export const CURRENT_VERSION = RELEASES[0].version;

// Release date and time in Eastern Time, e.g. "September 26, 2026 at 6:19 PM ET"
function formatReleased(iso) {
  const d    = new Date(iso);
  const date = d.toLocaleDateString('en-US', { timeZone: timeZone(), month: 'long', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { timeZone: timeZone(), hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time} ${tzLabel(d)}`;
}

// Notes are shown by level: each audience is seen by that level and above
// (all < managers < admins < super_admins). 'internal' never shows here.
// 3.68.0: Master Admins (between Admin and Super Admin) see Admins' notes, never Super Admins'
const LEVEL_RANK    = { user: 1, manager: 2, admin: 3, master_admin: 4, super_admin: 5 };
const AUDIENCE_RANK = { all: 1, managers: 2, admins: 3, master_admins: 4, super_admins: 5 };
const AUDIENCE_TAG  = { managers: 'Managers', admins: 'Admins', master_admins: 'Master Admins', super_admins: 'Super Admins' };

export default function WhatsNew({ level = 'user', onClose }) {
  const closeRef  = useRef(null);
  const returnRef = useRef(null);

  // 3.57.5: once, when the window opens (the parent passes a new onClose on
  // every render; re-running moved the cursor to ✕ on every refresh, e.g. a
  // fax pop-up, and the next Space/Enter closed the window)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      returnRef.current?.focus?.();
    };
  }, []);

  const rank = LEVEL_RANK[level] || 1;
  const visible = a => AUDIENCE_RANK[a] && AUDIENCE_RANK[a] <= rank;
  const releases  = RELEASES
    .map(r => ({ ...r, items: r.items.filter(it => visible(it.audience)) }))
    .filter(r => r.items.length);

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="whats-new-title">
        <div style={S.header} className="dlg-head">
          <div>
            <h2 id="whats-new-title" style={S.title}><Emo e="✨" /> What's new</h2>
            <div style={S.sub}>Fax Portal version {CURRENT_VERSION}</div>
          </div>
          <button ref={closeRef} onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={S.body}>
          {releases.map((r, i) => (
            <details key={r.version} open={i === 0} style={S.release}>
              <summary style={S.summary}>
                <span style={S.relTitle}>{r.title}</span>
                <span style={S.relMeta}>Version {r.version} · {r.dateLabel || formatReleased(r.releasedAt)}</span>
              </summary>
              <ul style={S.list}>
                {r.items.map((it, j) => (
                  <li key={j} style={S.item}>
                    {withIcons(it.text)}
                    {AUDIENCE_TAG[it.audience] && <span style={S.adminTag}>{AUDIENCE_TAG[it.audience]}</span>}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  modal: {
    background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(620px, 100%)', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)',
    animation: 'badgePop 0.2s ease-out'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0'
  },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  sub: { fontSize: 13, color: 'var(--t-78909c)', marginTop: 2 },
  closeBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0
  },
  body: { overflowY: 'auto', padding: '8px 22px 20px' },
  release: { borderBottom: '1px solid var(--c-eceff1)', padding: '12px 0' },
  summary: { cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, listStyle: 'revert' },
  relTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-263238)' },
  relMeta: { fontSize: 12, color: 'var(--t-78909c)' },
  list: { margin: '10px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 },
  item: { fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.55 },
  adminTag: {
    marginLeft: 8, fontSize: 10, fontWeight: 700, color: 'var(--t-6a1b9a)', background: 'var(--c-f3e5f5)',
    padding: '1px 7px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.4px'
  },
};
